// ============================================================================
// useQuickSearch — thin hook encapsulating the quick-search pre-fetch logic
//
// Provides `executeQuickSearch(query, maxResults)` which calls the
// `quickSearch` mutation with a 3-second AbortController timeout. On success
// it returns the `data.quickSearch` payload; on any failure (network error,
// GraphQL error, or timeout) it logs at warn level and returns null so the
// caller can fall through to the normal agent flow without interruption.
//
// Requirements: 8.3, 8.4
// ============================================================================

import { useCallback } from 'react';
import { quickSearch } from 'src/utilities/AgentChatAPIs';

/**
 * Hook that exposes a single `executeQuickSearch` function for pre-fetching
 * quick search results with a hard 3-second timeout.
 *
 * The returned function is wrapped in `useCallback` with an empty dependency
 * array — safe because it only closes over the module-level `quickSearch`
 * import. This ensures a stable reference so consumers (e.g. AgentChatProvider)
 * can include it in their own `useCallback` dependency arrays without causing
 * unnecessary re-renders.
 *
 * @returns {{ executeQuickSearch: (query: string, maxResults?: number) => Promise<object|null> }}
 */
const useQuickSearch = () => {
  /**
   * Execute a quick search call with a 3-second timeout.
   *
   * - On success: returns `data.quickSearch` (results + result_count).
   * - On timeout (>3s): aborts via AbortSignal, logs at warn level, returns null.
   * - On failure (network/GraphQL error): logs at warn level, returns null.
   *
   * @param {string} query – the user's search query
   * @param {number} [maxResults] – optional cap on results
   * @returns {Promise<object|null>} quick search payload or null on failure
   */
  const executeQuickSearch = useCallback(async (query, maxResults) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    try {
      const result = await quickSearch(query, maxResults, { signal: controller.signal });
      clearTimeout(timeout);
      return result?.data?.quickSearch ?? null;
    } catch (error) {
      clearTimeout(timeout);
      if (error?.name === 'AbortError') {
        console.warn('[QuickSearch] timed out after 3s');
      } else {
        console.warn('[QuickSearch] failed:', error?.name, error?.message);
      }
      return null;
    }
  }, []);

  return { executeQuickSearch };
};

export default useQuickSearch;
